/**
 * ユーザー管理画面
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, createUser, updateUser, deleteUser, toggleUserActive } from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Search, Loader2, UserX, UserCheck } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function Users() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');
  const [page, setPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', email: '', role: 'student', is_active: true });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', search, roleFilter, statusFilter, page],
    queryFn: () =>
      getUsers({
        search,
        role: roleFilter || undefined,
        is_active: statusFilter === '' ? undefined : statusFilter === 'active',
        page,
        limit: 20,
      }),
  });

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setIsCreateDialogOpen(false);
      setFormData({ name: '', email: '', role: 'student', is_active: true });
      toast({
        title: '成功',
        description: 'ユーザーを作成しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'ユーザーの作成に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      toast({
        title: '成功',
        description: 'ユーザーを更新しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'ユーザーの更新に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      toast({
        title: '成功',
        description: 'ユーザーを削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || 'ユーザーの削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: toggleUserActive,
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      const nowActive = result?.user?.is_active !== false;
      toast({
        title: '成功',
        description: nowActive ? 'ユーザーを有効にしました' : 'ユーザーを無効にしました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '有効/無効の切り替えに失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleCreate = () => {
    createMutation.mutate({ name: formData.name, email: formData.email, role: formData.role });
  };

  const handleEdit = (user: any) => {
    setSelectedUser(user);
    setFormData({ name: user.name, email: user.email, role: user.role, is_active: user.is_active !== false });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (selectedUser) {
      updateMutation.mutate({ id: selectedUser.id, data: formData });
    }
  };

  const handleDelete = (user: any) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedUser) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900">ユーザー管理</h1>
            <p className="text-base text-gray-500 mt-2">システムユーザーの管理</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                <Plus className="w-4 h-4 mr-1.5" />
                新規作成
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>新規ユーザー作成</DialogTitle>
                <DialogDescription>新しいユーザーを追加します</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">名前</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="email">メールアドレス</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="role">ロール</Label>
                  <select
                    id="role"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="student">学生</option>
                    <option value="educator">教育者</option>
                    <option value="admin">管理者</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  キャンセル
                </Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '作成'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* 検索・フィルタ */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="名前またはメールアドレスで検索..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-11 h-11 rounded-xl border-gray-200 bg-white"
                />
              </div>
            </div>
            <select
              className="h-11 px-4 pr-9 border border-gray-200 rounded-xl bg-white text-base font-medium text-gray-700 sm:w-64 appearance-none bg-no-repeat bg-[right_0.75rem_center]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
              }}
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">すべてのロール</option>
              <option value="admin">管理者</option>
              <option value="educator">教育者</option>
              <option value="student">学生</option>
            </select>
            <select
              className="h-11 px-4 pr-9 border border-gray-200 rounded-xl bg-white text-base font-medium text-gray-700 sm:w-48 appearance-none bg-no-repeat bg-[right_0.75rem_center]"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
              }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as '' | 'active' | 'inactive');
                setPage(1);
              }}
              aria-label="状態で絞り込み"
            >
              <option value="">すべての状態</option>
              <option value="active">有効のみ</option>
              <option value="inactive">無効のみ</option>
            </select>
          </div>
        </div>

        {/* ユーザー一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">ユーザー一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-200 hover:bg-transparent">
                    <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">名前</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">メールアドレス</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">ロール</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">状態</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-center">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.users?.map((user: any) => (
                    <TableRow key={user.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{user.id}</TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900">{user.name}</TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900">{user.email}</TableCell>
                      <TableCell className="py-4 text-center">
                        <RoleBadge role={user.role} />
                      </TableCell>
                      <TableCell className="py-4 text-center">
                        <AccountStatus active={user.is_active !== false} />
                      </TableCell>
                      <TableCell className="py-4 text-center text-base font-medium text-gray-900 tabular-nums">
                        {new Date(user.created).toLocaleString('ja-JP', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={() => toggleActiveMutation.mutate(user.id)}
                            disabled={toggleActiveMutation.isPending}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                            aria-label={user.is_active !== false ? '無効にする' : '有効にする'}
                            title={user.is_active !== false ? '無効にする(記録は残ります)' : '有効にする'}
                          >
                            {user.is_active !== false ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEdit(user)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                            aria-label="編集"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(user)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                            aria-label="削除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* ページネーション */}
              {data?.pagination && (
                <div className="flex justify-between items-center mt-5 pt-4">
                  <div className="text-base text-gray-700">
                    全 {data.pagination.total} 件中 {((page - 1) * 20 + 1)} - {Math.min(page * 20, data.pagination.total)} 件を表示
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      前へ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 編集ダイアログ */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>ユーザー編集</DialogTitle>
              <DialogDescription>ユーザー情報を編集します</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">名前</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-email">メールアドレス</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-role">ロール</Label>
                <select
                  id="edit-role"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="student">学生</option>
                  <option value="educator">教育者</option>
                  <option value="admin">管理者</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-is-active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="edit-is-active">有効(オフにするとログインできなくなります。記録は残ります)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                キャンセル
              </Button>
              <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '更新'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>ユーザーを削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。ユーザー「{selectedUser?.name}」を削除すると、面接記録・履歴・スコアも一緒に消えます。
                記録を残したまま利用を止めるには、削除ではなく「無効にする」を使ってください。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

function AccountStatus({ active }: { active: boolean }) {
  if (active) {
    return <span style={{ fontSize: 13, fontWeight: 700, color: '#52525B' }}>有効</span>;
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 64,
        padding: '5px 10px',
        borderRadius: 8,
        background: '#F4F4F5',
        color: '#52525B',
        border: '1px solid #52525B',
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      無効
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const meta =
    role === 'admin'
      ? { text: '管理者', bg: '#FFE4E6', color: '#BE123C' }
      : role === 'educator'
        ? { text: '教育者', bg: '#EDE9FE', color: '#6D28D9' }
        : { text: '学生', bg: '#F4F4F5', color: '#52525B' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 96,
        padding: '7px 10px',
        borderRadius: 8,
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}`,
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.text}
    </span>
  );
}

